import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AuthProvider } from "@/contexts/AuthContext";
import { SiteProvider } from "@/contexts/SiteContext";
import { FeaturesProvider } from "@/contexts/FeaturesContext";
import { DiscreetToggle } from "@/components/panel";

// Pagini noi (Academia + Sanctuar + Organismul viu) — încărcate lazy (code-splitting)
const Library = lazy(() => import("@/pages/Library"));
const SOS = lazy(() => import("@/pages/SOS"));
const SelfTest = lazy(() => import("@/pages/Test"));
const Garden = lazy(() => import("@/pages/Garden"));
const Forest = lazy(() => import("@/pages/Forest"));
const Breathe = lazy(() => import("@/pages/Breathe"));
const Stories = lazy(() => import("@/pages/Stories"));
const NightForest = lazy(() => import("@/pages/NightForest"));
const Certificate = lazy(() => import("@/pages/Certificate"));
const YearAlbum = lazy(() => import("@/pages/YearAlbum"));
const AdminStories = lazy(() => import("@/pages/admin/AdminStories"));
const AdminTestimonialSubmissions = lazy(() => import("@/pages/admin/AdminTestimonialSubmissions"));
const AdminForest = lazy(() => import("@/pages/admin/AdminForest"));
const AdminThanks = lazy(() => import("@/pages/admin/AdminThanks"));
const AdminForestWords = lazy(() => import("@/pages/admin/AdminForestWords"));
const AdminWise = lazy(() => import("@/pages/admin/AdminWise"));
const AdminClientMessages = lazy(() => import("@/pages/admin/AdminClientMessages"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminRewards = lazy(() => import("@/pages/admin/AdminRewards"));
const AdminSeeds = lazy(() => import("@/pages/admin/AdminSeeds"));
const AdminMiniCourse = lazy(() => import("@/pages/admin/AdminMiniCourse"));
const AdminTree = lazy(() => import("@/pages/admin/AdminTree"));
const AdminClientJourney = lazy(() => import("@/pages/admin/AdminClientJourney"));
const Booking = lazy(() => import("@/pages/Booking"));
const Sessions1on1 = lazy(() => import("@/pages/Sessions1on1"));

// Public layout & pages
import PublicLayout from "@/components/layout/PublicLayout";
import ScrollToTop from "@/components/layout/ScrollToTop";
import SEOHead from "@/components/layout/SEOHead";
import AnalyticsLoader from "@/components/analytics/AnalyticsLoader";
import Home from "@/pages/Home";
import About from "@/pages/About";
import ServicesPage from "@/pages/Services";
import Courses from "@/pages/Courses";
const CourseDetail = lazy(() => import("@/pages/CourseDetail"));
const Audio = lazy(() => import("@/pages/Audio"));
const Live = lazy(() => import("@/pages/Live"));
import { CommunityPage, ReviewsPage, DiplomasPage, DownloadsPage, ContactPage } from "@/pages/SimplePages";
const SubmitReview = lazy(() => import("@/pages/SubmitReview"));
const CourseLearn = lazy(() => import("@/pages/CourseLearn"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const DynamicPage = lazy(() => import("@/pages/DynamicPage"));
import Techniques, { TechniqueDetail } from "@/pages/Techniques";
import Checkout, { CheckoutConfirmation } from "@/pages/Checkout";
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
import Promotions, { PromoBanner } from "@/pages/Promotions";

// Auth
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const MyAccount = lazy(() => import("@/pages/MyAccount"));

// Admin — încărcate lazy (code-splitting) ca să nu îngreuneze bundle-ul public
import AdminLogin from "@/pages/admin/AdminLogin";
import ProblemDetail from "@/pages/ProblemDetail";
const AdminLayout = lazy(() => import("@/components/layout/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminSections = lazy(() => import("@/pages/admin/AdminSections"));
const AdminTheme = lazy(() => import("@/pages/admin/AdminTheme"));
const AdminFiles = lazy(() => import("@/pages/admin/AdminFiles"));
const AdminCourses = lazy(() => import("@/pages/admin/AdminCourses"));
const AdminCourseAnalytics = lazy(() => import("@/pages/admin/AdminCourseAnalytics"));
const AdminLiveEditor = lazy(() => import("@/pages/admin/AdminLiveEditor"));
const AdminRevenueStats = lazy(() => import("@/pages/admin/AdminRevenueStats"));
const AdminProblems = lazy(() => import("@/pages/admin/AdminProblems"));
const AdminCollection = lazy(() => import("@/pages/admin/AdminCollection"));
const AdminContacts = lazy(() => import("@/pages/admin/AdminContacts"));
const AdminSubscribers = lazy(() => import("@/pages/admin/AdminSubscribers"));
const AdminTechniques = lazy(() => import("@/pages/admin/AdminTechniques"));
const AdminNotifications = lazy(() => import("@/pages/admin/AdminNotifications"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminAgenda = lazy(() => import("@/pages/admin/AdminAgenda"));
const PreviewMode = lazy(() => import("@/components/admin/PreviewMode"));

import "@/App.css";

// Pagina 404 — URL necunoscut, cu drum înapoi spre secțiunile principale.
function NotFound() {
  return (
    <div className="py-32 text-center max-w-md mx-auto px-5" data-testid="not-found">
      <p className="text-[10px] uppercase tracking-[0.22em] mb-2" style={{ color: "var(--ct-gold)" }}>Eroare 404</p>
      <h1 className="font-serif text-3xl mb-3">Pagina nu a fost găsită</h1>
      <p className="text-sm mb-6" style={{ color: "var(--ct-text-muted)" }}>
        Pagina pe care o cauți nu există sau a fost mutată.
      </p>
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <a href="/" className="btn-gold">Acasă</a>
        <a href="/sedinte" className="btn-outline-gold">Ședințe</a>
      </div>
    </div>
  );
}

function AppShell() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <SEOHead />
      <AnalyticsLoader />
      <Suspense fallback={null}>
        <PreviewMode />
      </Suspense>
      <ErrorBoundary>
      <Suspense fallback={<div className="py-32 text-center" style={{ color: "var(--ct-text-muted, #888)" }}>Se încarcă...</div>}>
      <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/despre-mine" element={<About />} />
        <Route path="/tehnici" element={<Techniques />} />
        <Route path="/tehnici/:slug" element={<TechniqueDetail />} />
        <Route path="/vindecare/:slug" element={<ProblemDetail />} />
        <Route path="/servicii" element={<ServicesPage />} />
        <Route path="/cursuri" element={<Courses />} />
        <Route path="/biblioteca" element={<Library />} />
        <Route path="/test" element={<SelfTest />} />
        <Route path="/cursuri/:slug" element={<CourseDetail />} />
        <Route path="/audio" element={<Audio />} />
        <Route path="/live" element={<Live />} />
        <Route path="/comunitate" element={<CommunityPage />} />
        <Route path="/padurea" element={<Forest />} />
        <Route path="/povesti" element={<Stories />} />
        <Route path="/certificat/:courseId" element={<Certificate />} />
        <Route path="/albumul-meu" element={<YearAlbum />} />
        <Route path="/copacul-meu" element={<Garden />} />
        <Route path="/gradina" element={<Garden />} />{/* alias vechi */}
        <Route path="/programare" element={<Booking />} />
        <Route path="/lucreaza-cu-cristian" element={<Sessions1on1 />} />
        <Route path="/sedinte" element={<Sessions1on1 />} />
        <Route path="/recenzii" element={<ReviewsPage />} />
        <Route path="/recenzii/lasa-o-recenzie" element={<SubmitReview />} />
        <Route path="/diplome" element={<DiplomasPage />} />
        <Route path="/descarcari" element={<DownloadsPage />} />
        <Route path="/resurse-gratuite" element={<DownloadsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/p/:slug" element={<DynamicPage />} />
        <Route path="/promotii" element={<Promotions />} />
        <Route path="/legal/:slug" element={<LegalPage />} />
        <Route path="/termeni" element={<LegalPage />} />
        <Route path="/politica-confidentialitate" element={<LegalPage />} />
        <Route path="/politica-cookies" element={<LegalPage />} />
        <Route path="/gdpr" element={<LegalPage />} />
        <Route path="/disclaimer" element={<LegalPage />} />
        <Route path="/dezabonare" element={<Unsubscribe />} />
        <Route path="/login" element={<Login />} />
        <Route path="/inregistrare" element={<Register />} />
        <Route path="/register" element={<Navigate to="/inregistrare" replace />} />
        <Route path="/reset-parola" element={<ResetPassword />} />
        <Route path="/cont" element={<MyAccount />} />
        <Route path="*" element={<NotFound />} />
      </Route>

      <Route path="/checkout" element={<Checkout />} />
      <Route path="/checkout/succes" element={<CheckoutSuccess />} />
      <Route path="/confirmare" element={<CheckoutConfirmation />} />
      <Route path="/cursuri/:slug/invata" element={<CourseLearn />} />
      <Route path="/sos" element={<SOS />} />
      <Route path="/respiram" element={<Breathe />} />
      <Route path="/noaptea" element={<NightForest />} />

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="setari" element={<AdminSettings />} />
          <Route path="sectiuni" element={<AdminSections />} />
          <Route path="editor" element={<AdminLiveEditor />} />
          <Route path="tema" element={<AdminTheme />} />
          <Route path="fisiere" element={<AdminFiles />} />
          <Route path="cursuri" element={<AdminCourses />} />
          <Route path="probleme" element={<AdminProblems />} />
          <Route path="cursuri/:courseId/analytics" element={<AdminCourseAnalytics />} />
          <Route path="tehnici" element={<AdminTechniques />} />
          <Route path="notificari" element={<AdminNotifications />} />
          <Route path="recenzii" element={<AdminCollection collection="reviews" />} />
          <Route path="marturii-primite" element={<AdminTestimonialSubmissions />} />
          <Route path="diplome" element={<AdminCollection collection="diplomas" />} />
          <Route path="descarcari" element={<AdminCollection collection="downloads" />} />
          <Route path="audio" element={<AdminCollection collection="audios" />} />
          <Route path="live" element={<AdminCollection collection="events" />} />
          <Route path="promotii" element={<AdminCollection collection="promotions" />} />
          <Route path="pagini" element={<AdminCollection collection="pages" />} />
          <Route path="mesaje" element={<AdminContacts />} />
          <Route path="abonati" element={<AdminSubscribers />} />
          <Route path="utilizatori" element={<AdminUsers />} />
          <Route path="agenda" element={<AdminAgenda />} />
          <Route path="comenzi" element={<AdminOrders />} />
          <Route path="recompense" element={<AdminRewards />} />
          <Route path="seminte" element={<AdminSeeds />} />
          <Route path="povesti" element={<AdminStories />} />
          <Route path="multumiri" element={<AdminThanks />} />
          <Route path="mini-curs" element={<AdminMiniCourse />} />
          <Route path="copac" element={<AdminTree />} />
          <Route path="padurea" element={<AdminForest />} />
          <Route path="cuvinte" element={<AdminForestWords />} />
          <Route path="indrumare" element={<AdminWise />} />
          <Route path="mesaje-clienti" element={<AdminClientMessages />} />
          <Route path="client/:userId" element={<AdminClientJourney />} />
          <Route path="statistici" element={<AdminRevenueStats />} />
          {/* Legacy redirects → new unified Agenda */}
          <Route path="clienti" element={<AdminAgenda />} />
          <Route path="programari" element={<AdminAgenda />} />
          <Route path="rezervari" element={<AdminAgenda />} />
        </Route>
      </Routes>
      </Suspense>
      </ErrorBoundary>
      <DiscreetToggle />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <SiteProvider>
        <AuthProvider>
          <FeaturesProvider>
            <BrowserRouter>
              <AppShell />
            </BrowserRouter>
          </FeaturesProvider>
        </AuthProvider>
      </SiteProvider>
      <Toaster position="top-right" theme="light" richColors />
    </div>
  );
}

export default App;
